import { useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import * as checkoutCartSelectors from 'dux/checkoutCart/selectors';
import * as GWPSelectors from 'dux/giftWithPurchase/selectors';

const Root = styled.div<{ isFixedWidth: boolean }>`
  ${({ isFixedWidth }) => isFixedWidth && 'max-width: 378px;'}

  margin-inline: ${legacyTheme.spacing.s24};
  margin-block: 0;

  background: ${theme.colors.neutral[100]};
  box-shadow: ${`2px 2px 3px 0 ${theme.colors.neutral[600]}`};

  ${legacyTheme.breakpoints.match('md')} {
    width: 100%;
  }
`;

const CardBanner = styled.div<{ isUnlocked: boolean }>`
  text-align: center;
  font-size: min(0.7rem, 3vw);
  letter-spacing: 0.12em;
  padding-top: 0.25em;
  padding-bottom: 0.15em;

  transition: all 0.3s ease;

  color: ${theme.colors.neutral[900]};
  background-color: ${({ isUnlocked }) =>
    isUnlocked ? theme.colors.highlight[200] : theme.colors.highlight[100]};
`;

const CardBody = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: var(--image-column-width) var(--text-column-width);
  column-gap: var(--card-grid-column-gap, ${legacyTheme.spacing.s24});
  align-items: center;
`;

const Wrapper = styled.div`
  padding: ${legacyTheme.spacing.s16} ${legacyTheme.spacing.s24};
`;

const Image = styled.img`
  align-self: start;
  width: 100%;

  ${legacyTheme.breakpoints.match('md')} {
    max-width: 100px;
  }
`;

const MainInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
`;

const DescriptionText = styled(Typography)`
  &:not(:last-of-type) {
    margin-bottom: 1em;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-right: 3px;
  }
`;

const ConditionText = styled(DescriptionText)`
  font-style: italic;
`;

type GWPBlockProps = {
  containerClasse?: string;
  fixedWidth?: boolean;
  offeredVariantType: string;
};

export const GWPBlock = ({
  containerClasse = '',
  fixedWidth = false,
  offeredVariantType,
}: GWPBlockProps) => {
  const gwpItemData = useAppSelector((state) =>
    GWPSelectors.getGiftWithPurchaseByOfferedVariantType(state, offeredVariantType),
  );
  const isUnlocked = useAppSelector((state) =>
    checkoutCartSelectors.getGWPisUnlocked(state, { offeredVariantType }),
  );

  const { isMobile } = useResponsiveVariant();

  const cardDescription = (
    <>
      <DescriptionText color="grey" markupName="p" variant="p3">
        {gwpItemData?.promotion_description}
      </DescriptionText>
      <ConditionText color="grey" markupName="p" variant="p3">
        {gwpItemData?.cart_condition_text}
      </ConditionText>
    </>
  );

  return (
    <Root className={containerClasse} isFixedWidth={fixedWidth}>
      <CardBanner isUnlocked={isUnlocked}>
        {isUnlocked
          ? 'FREE GIFT ADDED TO YOUR CART'
          : gwpItemData?.cart_banner_text?.toUpperCase?.()}
      </CardBanner>
      <Wrapper>
        <CardBody>
          <Image alt={gwpItemData?.promotion_title} src={gwpItemData?.picture_name} />
          <MainInfo>
            <Typography color="noir" markupName="h2" paragraph variant="h3">
              {gwpItemData?.promotion_title}
            </Typography>
            {!isMobile && cardDescription}
          </MainInfo>
        </CardBody>
        {isMobile && cardDescription}
      </Wrapper>
    </Root>
  );
};
